import { useEffect, useRef, useState } from "react";
import { ConfirmModal } from "../components/ConfirmModal.jsx";
import { SecretWords, TermBoards } from "../components/games/TermBoards.jsx";
import {
  GoogleAccountButton,
  useSoloAuth,
} from "../contexts/SoloAuthContext.jsx";
import { SoundButton, useSound } from "../contexts/SoundContext.jsx";
import { TERM_WORDS } from "../data/termWords.js";
import { drawTermWords, maxTermAttempts } from "../domain/term.js";
import { useTermEngine } from "../hooks/useTermEngine.js";
import {
  registrarVitoriaTermo,
  salvarRecordeTermo,
} from "../services/ranking.js";

const SESSION_KEY = "@PartyGames:termoSoloSessao";
const readSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw);
    return ["jogando", "fim"].includes(value?.etapa) ? value : null;
  } catch {
    return null;
  }
};
const removeSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {}
};

export function TermSoloPage({ aoVoltar }) {
  const { googleUser } = useSoloAuth();
  const { tocarSom, mutado } = useSound();
  const restored = useRef(readSession()).current;
  const restoredSeconds = restored
    ? restored.etapa === "fim"
      ? restored.tempoFinal || 0
      : Math.max(
          0,
          Math.floor((Date.now() - (restored.inicioEm || Date.now())) / 1000),
        )
    : 0;
  const [stage, setStage] = useState(restored?.etapa || "selecao");
  const [mode, setMode] = useState(restored?.modoTermo || 1);
  const [secrets, setSecrets] = useState(restored?.palavrasSecretas || []);
  const [seconds, setSeconds] = useState(restoredSeconds);
  const secondsRef = useRef(restoredSeconds);
  const startedAt = useRef(restored?.inicioEm || Date.now());
  const [victory, setVictory] = useState(Boolean(restored?.venceu));
  const [rankingSaved, setRankingSaved] = useState(
    Boolean(restored?.salvouRanking),
  );
  const [exitOpen, setExitOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const correctAudio = useRef(null);
  const wrongAudio = useRef(null);

  const finish = (won) => {
    setVictory(won);
    setStage("fim");
    tocarSom(won ? correctAudio : wrongAudio, won ? 0.6 : undefined);
  };
  const engine = useTermEngine({
    secrets,
    maxAttempts: maxTermAttempts(mode),
    resetKey,
    initialGuesses: restored?.palpites || [],
    initialFinished: restored?.etapa === "fim",
    onFinish: finish,
  });

  useEffect(() => {
    document.body.classList.toggle("term-active", stage === "jogando");
    return () => document.body.classList.remove("term-active");
  }, [stage]);

  useEffect(() => {
    if (stage !== "jogando") return undefined;
    const interval = window.setInterval(() => {
      setSeconds((current) => {
        const next = current + 1;
        secondsRef.current = next;
        return next;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [stage]);

  useEffect(() => {
    try {
      if (stage === "jogando" || stage === "fim") {
        localStorage.setItem(
          SESSION_KEY,
          JSON.stringify({
            etapa: stage,
            modoTermo: mode,
            palavrasSecretas: secrets,
            palpites: engine.guesses,
            venceu: victory,
            salvouRanking: rankingSaved,
            inicioEm: startedAt.current,
            tempoFinal: stage === "fim" ? secondsRef.current : null,
          }),
        );
      } else removeSession();
    } catch {}
  }, [stage, mode, secrets, engine.guesses, victory, rankingSaved]);

  useEffect(() => {
    if (
      stage !== "fim" ||
      !victory ||
      !googleUser ||
      rankingSaved ||
      engine.guesses.length === 0
    ) {
      return;
    }
    Promise.all([
      salvarRecordeTermo(
        googleUser,
        mode,
        engine.guesses.length,
        secondsRef.current,
      ),
      registrarVitoriaTermo(googleUser, mode),
    ])
      .then(() => setRankingSaved(true))
      .catch((error) => console.error("Erro ao salvar ranking:", error));
  }, [stage, victory, googleUser, rankingSaved, mode, engine.guesses.length]);

  const start = (nextMode) => {
    setMode(nextMode);
    setSecrets(drawTermWords(TERM_WORDS, nextMode));
    setVictory(false);
    setRankingSaved(false);
    setSeconds(0);
    secondsRef.current = 0;
    startedAt.current = Date.now();
    setResetKey((value) => value + 1);
    setStage("jogando");
  };
  const leave = () => {
    removeSession();
    aoVoltar();
  };

  if (stage === "selecao") {
    return (
      <div className="container-sm text-center term-solo-selection">
        <button className="term-back" onClick={aoVoltar} type="button">
          ← Voltar
        </button>
        <h2 className="title">🕹️ Termo Solo</h2>
        <p className="subtitle mb-40">
          Escolha o modo e jogue sozinho, no seu ritmo.
        </p>
        <GoogleAccountButton />
        <div className="card term-mode-list">
          {[
            [1, "Termo (1 Palavra)", "6 tentativas"],
            [2, "Dueto (2 Palavras)", "7 tentativas"],
            [4, "Quarteto (4 Palavras)", "9 tentativas"],
          ].map(([value, label, description]) => (
            <button
              className="btn btn-primary"
              onClick={() => start(value)}
              type="button"
              key={value}
            >
              <span>{label}</span>
              <small>{description}</small>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (stage === "fim") {
    return (
      <div className="container-sm text-center term-solo-result">
        <div className={`card term-result ${victory ? "won" : "lost"}`}>
          <h3>{victory ? "🎉 Você acertou!" : "❌ Fim de tentativas!"}</h3>
          <SecretWords secrets={secrets} label="Palavra(s) secreta(s):" />
          <div className="term-stats">
            <div>
              <strong>{engine.guesses.length}</strong>
              <span>tentativas</span>
            </div>
            <div>
              <strong>{seconds}s</strong>
              <span>tempo</span>
            </div>
          </div>
          {victory &&
            (googleUser ? (
              rankingSaved && (
                <p className="info-box">🏆 Resultado salvo no Ranking!</p>
              )
            ) : (
              <div className="term-login-prompt">
                <p>Entre com Google para salvar esse resultado:</p>
                <GoogleAccountButton />
              </div>
            ))}
          <div className="term-result-actions">
            <button
              className="btn btn-primary"
              onClick={() => start(mode)}
              type="button"
            >
              🔁 Jogar de Novo
            </button>
            <button
              className="btn term-secondary"
              onClick={() => {
                removeSession();
                setStage("selecao");
              }}
              type="button"
            >
              🔀 Trocar Modo
            </button>
            <button className="btn term-exit" onClick={leave} type="button">
              Sair
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="termo-wrapper termo-solo-wrapper">
      <audio
        ref={correctAudio}
        src="/sons/acerto.mp3"
        preload="auto"
        muted={mutado}
      />
      <audio
        ref={wrongAudio}
        src="/sons/erro.mp3"
        preload="auto"
        muted={mutado}
      />
      <header className="term-game-header">
        <button
          className="term-header-exit"
          onClick={() => setExitOpen(true)}
          type="button"
        >
          🚪 Sair
        </button>
        <div>
          <SoundButton variante="inline" />
          <h2>🕹️ Termo Solo</h2>
        </div>
        <strong>⏱️ {seconds}s</strong>
      </header>
      <ConfirmModal
        isOpen={exitOpen}
        title="Sair da partida?"
        message="Seu progresso nessa partida será perdido e não contará para o ranking."
        confirmText="Sim, sair"
        onConfirm={() => {
          setExitOpen(false);
          leave();
        }}
        onCancel={() => setExitOpen(false)}
      />
      {engine.error && <div className="term-error">{engine.error}</div>}
      <TermBoards
        secrets={secrets}
        maxAttempts={maxTermAttempts(mode)}
        engine={engine}
      />
    </div>
  );
}
